'use strict';

/**
 * NACRE Wave 2.5.2 — module-vault-client (browser-side)
 *
 * The "smart" half of the module vault system. Runs in the browser.
 * Encrypts data with a per-module master key, ships the resulting
 * opaque envelope to the server via an injected fetcher, and decrypts
 * after fetching. The server (vault-blob-store) only sees ciphertext.
 *
 * Two operations:
 *   - saveModuleData({moduleMaster, moduleId, data, putBlob})
 *   - loadModuleData({moduleMaster, moduleId, fetchBlob}) → data | null
 *
 * Plus a convenience adapter:
 *   - createSaveLoadAdapter({...}) → {save, load}
 *     Module code can pass {save, load} to its data layer instead of
 *     having to plumb the moduleMaster + fetchers through every call.
 *
 * Pure browser-safe: no Node-only deps. Uses our symmetric primitive
 * which uses Node's `crypto` builtin (Node) or the equivalent SubtleCrypto
 * (browser via a future shim).
 *
 * The fetcher functions are INJECTED so this module never imports HTTP
 * or fs. Tests pass sync in-memory stubs; the browser wires real fetch().
 *
 * Expected fetcher signatures:
 *   fetchBlob(moduleId): Promise<envelope | null>
 *   putBlob(moduleId, envelope): Promise<void>
 */

const symmetric = require('./symmetric');

/**
 * Encrypt + ship via fetcher.
 *
 * @param {object} args
 * @param {Uint8Array|Buffer} args.moduleMaster — 32 bytes
 * @param {string} args.moduleId
 * @param {*} args.data — any JSON-serializable value
 * @param {function(string, object): Promise<void>} args.putBlob
 */
async function saveModuleData({ moduleMaster, moduleId, data, putBlob }) {
  if (typeof putBlob !== 'function') {
    throw new TypeError('putBlob must be a function');
  }
  if (typeof moduleId !== 'string' || moduleId.length === 0) {
    throw new TypeError('moduleId must be a non-empty string');
  }
  const json = JSON.stringify(data === undefined ? null : data);
  const envelope = symmetric.encrypt(json, moduleMaster);
  await putBlob(moduleId, envelope);
}

/**
 * Fetch + decrypt via fetcher.
 *
 * @param {object} args
 * @param {Uint8Array|Buffer} args.moduleMaster — 32 bytes
 * @param {string} args.moduleId
 * @param {function(string): Promise<object|null>} args.fetchBlob
 * @returns {Promise<*|null>} the decrypted data, or null if no blob exists
 */
async function loadModuleData({ moduleMaster, moduleId, fetchBlob }) {
  if (typeof fetchBlob !== 'function') {
    throw new TypeError('fetchBlob must be a function');
  }
  if (typeof moduleId !== 'string' || moduleId.length === 0) {
    throw new TypeError('moduleId must be a non-empty string');
  }
  const envelope = await fetchBlob(moduleId);
  if (envelope === null || envelope === undefined) {
    return null;
  }
  const json = symmetric.decrypt(envelope, moduleMaster);
  return JSON.parse(json);
}

/**
 * Build a {save, load} pair pre-bound to a specific moduleMaster + moduleId
 * + fetcher pair. Convenience for module integrations.
 *
 * @returns {{save: function(*): Promise<void>, load: function(): Promise<*|null>}}
 */
function createSaveLoadAdapter({ moduleMaster, moduleId, fetchBlob, putBlob }) {
  // ── A1 AS A PROPERTY: no whole-blob REPLACE until this adapter has READ ──
  //
  // `save` is a whole-blob replace. A caller that never read, or whose read
  // THREW, does not know what is stored — so the value it hands over is a
  // locally synthesized default (an empty array, or a stale legacy copy), and
  // writing it destroys ciphertext HGE cannot restore.
  //
  // The A1 lane closed this in six client files by giving each its own
  // load-provenance flag. That is a convention repeated across files, which is
  // a deadline rather than a property (the T881-P3 / T883 lesson): a seventh
  // consumer inherits nothing. Enforcing it HERE means every consumer —
  // including the two control-module executors, which are safe today only
  // because their `ensureLoaded` happens to rethrow — inherits the refusal.
  //
  // Per-adapter, deliberately. Module-global state would let one module's
  // healthy read license another's blind write; a fresh adapter (which
  // `ensureAdapter()` builds whenever the session master changes) must earn
  // its own licence.
  let loadCompleted = false;
  return {
    save: (data) => {
      if (!loadCompleted) {
        // THROW, never a silent no-op: a swallowed refusal leaves the caller —
        // and the next person to edit it — believing the write landed.
        const err = new Error(
          'ERR_VAULT_SAVE_BEFORE_LOAD: refusing to replace the whole "' + moduleId +
          '" blob before a successful read. Prior contents are unknown, so this' +
          ' write could destroy stored data. Call load() first, or use' +
          ' saveModuleData() directly if an unpaired write is genuinely intended.');
        err.code = 'ERR_VAULT_SAVE_BEFORE_LOAD';
        err.moduleId = moduleId;
        return Promise.reject(err);
      }
      return saveModuleData({ moduleMaster, moduleId, data, putBlob });
    },
    load: async () => {
      const result = await loadModuleData({ moduleMaster, moduleId, fetchBlob });
      // Set only on the success path. `result === null` is the server's
      // definitive "no blob for this account yet" (fetchBlob maps 404 → null,
      // and loadModuleData returns null WITHOUT throwing) — that is an ANSWER,
      // so it licenses the first write. A throw is not an answer, and leaves
      // the licence unset so a retry can still earn it.
      loadCompleted = true;
      return result;
    },
  };
}

module.exports = {
  saveModuleData,
  loadModuleData,
  createSaveLoadAdapter,
};
